/**
 * Export.
 *
 * This is where the product's claim becomes true or stays a claim. The Edge
 * Integrity pipeline has been written and tested since early on and wired to
 * nothing — running it here is what turns "clothes that don't fray at the
 * seams" from a document into a file.
 *
 * Order matters and is not obvious: the pipeline runs on a COPY, never on the
 * live paint surface. Dilation writes into the gutters and floods the dead
 * space, so running it in place would permanently bake export artefacts into
 * the artwork the user is still editing.
 *
 * And the result never touches a canvas again. See shared/png.ts — a 2D canvas
 * premultiplies, so `putImageData` + `toBlob` zeroes the colour under every
 * transparent pixel, which is precisely what the colour-bleed stage writes. The
 * pipeline ran correctly and the file did not contain its output.
 */

import { type Coverage, coverageOf } from "../../shared/coverage";
import { type Bitmap, runEdgePipeline } from "../../shared/edge";
import {
  CANVAS,
  DEFAULT_EDGE_SETTINGS,
  type EdgeSettings,
  type GarmentType,
} from "../../shared/format";
import { encodePng } from "../../shared/png";
import { checkSeams, type SeamCheck } from "../../shared/seam-check";
import type { SeamMap } from "../../shared/seam-map";

export interface Preflight {
  ok: boolean;
  width: number;
  height: number;
  bytes: number;
  /** Opaque pixels added outside the regions — dilation into the gutter. */
  bledPixels: number;
  /**
   * Transparent pixels that gained colour — the halo fix.
   *
   * Counted separately because the two stages fail independently, and because
   * this is the number that silently read zero for as long as the export went
   * through a canvas.
   */
  haloPixels: number;
  problems: string[];
}

export interface ExportResult {
  blob: Blob;
  preflight: Preflight;
}

/** Roblox rejects anything that is not exactly this. */
const REQUIRED = { width: CANVAS.width, height: CANVAS.height };

/** Uploads over this get slow and are usually a mistake. */
const SIZE_WARN_BYTES = 1024 * 1024;

function opaqueCount(b: Bitmap): number {
  let n = 0;
  for (let i = 3; i < b.data.length; i += 4) if (b.data[i]) n++;
  return n;
}

/** Transparent pixels carrying colour: alpha 0, RGB not black. */
function haloCount(b: Bitmap): number {
  let n = 0;
  for (let i = 0; i < b.data.length; i += 4) {
    if (
      (b.data[i + 3] as number) === 0 &&
      (b.data[i] as number) | (b.data[i + 1] as number) | (b.data[i + 2] as number)
    ) {
      n++;
    }
  }
  return n;
}

/**
 * Run the pipeline and produce the upload-ready PNG.
 *
 * PNG specifically, never WebP: Roblox accepts PNG and JPG only, and a
 * well-meaning "optimise everything to WebP" step would break every download.
 * JPG is out too — it has no alpha, and transparency is how short sleeves work.
 */
export async function exportTemplate(
  source: HTMLCanvasElement,
  settings: EdgeSettings = DEFAULT_EDGE_SETTINGS,
  /**
   * The body's seam pairing, for pipeline stage 2. Omit and the stage is
   * skipped — the file is still correct, just not seam-corrected.
   */
  seam: SeamMap | null = null,
): Promise<ExportResult> {
  const ctx = source.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable");

  const image = ctx.getImageData(0, 0, source.width, source.height);
  // ImageData and Bitmap are the same shape by design, so this is a view rather
  // than a conversion — but it is a copy of the surface, which is the point.
  const bitmap: Bitmap = {
    width: image.width,
    height: image.height,
    data: image.data,
  };

  const before = opaqueCount(bitmap);
  runEdgePipeline(bitmap, settings, seam);
  const after = opaqueCount(bitmap);

  // Straight from the pipeline's bytes to the file. No canvas in between —
  // that round trip is what silently discarded the colour bleed.
  const png = await encodePng(bitmap.data, bitmap.width, bitmap.height);
  const blob = new Blob([png as BlobPart], { type: "image/png" });

  const problems: string[] = [];
  if (bitmap.width !== REQUIRED.width || bitmap.height !== REQUIRED.height) {
    problems.push(
      `Must be exactly ${REQUIRED.width}x${REQUIRED.height}. This is ${bitmap.width}x${bitmap.height}.`,
    );
  }
  if (blob.size > SIZE_WARN_BYTES) {
    problems.push(`${(blob.size / 1024 / 1024).toFixed(2)} MB — over 1 MB uploads slowly.`);
  }

  return {
    blob,
    preflight: {
      ok: problems.length === 0,
      width: bitmap.width,
      height: bitmap.height,
      bytes: blob.size,
      bledPixels: Math.max(0, after - before),
      haloPixels: haloCount(bitmap),
      problems,
    },
  };
}

/** Save the blob. Revoking on the next task avoids leaking the object URL. */
export function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Roblox has no filename rules, but a colliding download is its own annoyance. */
export function filenameFor(title: string, garment = "shirt"): string {
  const slug =
    title
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "untitled";

  /*
   * The garment is named once, not twice.
   *
   * An unnamed design is called "Untitled pants", which produced
   * `untitled-pants-pants-585x559.png` — and anyone who names a design
   * "Varsity Pants" got the same stutter. The suffix is still added when the
   * name does not already end in it, because the whole point is that a shirt
   * and a pair of pants are indistinguishable from their pixels.
   */
  const named = slug === garment || slug.endsWith(`-${garment}`);
  const base = named ? slug : `${slug}-${garment}`;

  // The size stays in the name. Wrong dimensions are a top cause of rejected
  // uploads, and this is the one place it can be confirmed without opening
  // anything — worth the characters.
  return `${base}-${CANVAS.width}x${CANVAS.height}.png`;
}

/**
 * Everything the export sheet shows, from one run of the pipeline.
 *
 * The sheet used to be impossible to build honestly: showing preflight numbers
 * meant running the pipeline once, and pressing Export ran it again. Two runs
 * means the receipt on screen describes a *different* file from the one that
 * downloads — normally identical, but not if the canvas moved in between, and
 * always twice the work on a design where the pipeline is the slow part.
 *
 * So the sheet holds this. The button downloads `blob` rather than producing
 * one, and every number beside it was measured from those exact bytes.
 *
 * `preview` is that same blob as an object URL — the preview *is* the file,
 * not a re-render of it, so there is no way for them to disagree. It has to be
 * released; see `dispose`.
 */
export interface PreparedExport {
  blob: Blob;
  preflight: Preflight;
  coverage: Coverage;
  /**
   * Where the artwork disagrees with itself across a fold, or null when there
   * is no body to compare against.
   *
   * Distinct from `coverage`, which counts what was left bare. This counts what
   * was painted on both sides and does not match — the seam the edge pipeline
   * cannot fix, because there is no correct answer between two colours the
   * artist chose separately.
   */
  seams: SeamCheck | null;
  filename: string;
  /** Object URL for `blob`. Valid until `dispose()`. */
  preview: string;
  /** Revoke the object URL. Safe to call twice. */
  dispose(): void;
}

export async function prepareExport(
  source: HTMLCanvasElement,
  opts: {
    title: string;
    garment: GarmentType;
    edges?: EdgeSettings;
    /**
     * The body to seam-correct against.
     *
     * Body-specific by nature: all five shipped rigs pair their faces
     * differently, so there is no reference geometry to fall back on. Omitted,
     * stage 2 is skipped.
     */
    body?: string;
  },
): Promise<PreparedExport> {
  /*
    Resolved here rather than by the caller, so every export path gets it
    without each one remembering to. A t-shirt has no 3D layout to correct, so
    it asks for nothing.
  */
  let seam: SeamMap | null = null;
  if (opts.body && (opts.garment === "shirt" || opts.garment === "pants")) {
    const { seamMapFor } = await import("./seam-source");
    seam = await seamMapFor(opts.body, opts.garment);
  }
  const { blob, preflight } = await exportTemplate(
    source,
    opts.edges ?? DEFAULT_EDGE_SETTINGS,
    seam,
  );

  /*
   * Coverage is measured on the artwork, before the pipeline.
   *
   * It would be the same afterwards — dilation only writes into the gutters,
   * which are outside every region, and the colour bleed writes colour under
   * transparent pixels while leaving their alpha at zero. Reading the source is
   * simply the honest input: what the person drew.
   */
  const ctx = source.getContext("2d");
  const pixels = ctx ? ctx.getImageData(0, 0, source.width, source.height) : null;
  const coverage = pixels ? coverageOf(pixels) : emptyCoverage();
  /*
    Read from the SOURCE, before the pipeline, for a reason the coverage note
    above does not cover: stage 2b would erase the evidence.

    `seamBlend` averages the two sides of a fold over six texels, so measuring
    the output would find the disagreement already half-hidden and report a
    smaller break than the artist actually drew. The whole point of this number
    is that it describes the artwork, not what we did to it.
  */
  const seams =
    pixels && seam
      ? checkSeams({ width: pixels.width, height: pixels.height, data: pixels.data }, seam)
      : null;

  const preview = URL.createObjectURL(blob);
  let released = false;

  return {
    blob,
    preflight,
    coverage,
    seams,
    filename: filenameFor(opts.title, opts.garment),
    preview,
    dispose() {
      if (released) return;
      released = true;
      URL.revokeObjectURL(preview);
    },
  };
}

/** A 2D context is unavailable. Nothing to measure, and not worth failing over. */
function emptyCoverage(): Coverage {
  return { regionPixels: 0, barePixels: 0, bareRatio: 0, regions: [], emptyRegions: [] };
}
